import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { type InboxesStore } from 'entities/Inbox/store/inboxes'
import { ChannelInbox, ChannelInboxConversation, type Websocket } from 'entities/WebSocket'

export class ChannelsInboxes {
  private _channelsMap: Map<
    number,
    { inbox: ChannelInbox; conversation: ChannelInboxConversation }
  > = new Map()
  private _disposeSubscribe: IReactionDisposer | null = null

  constructor(private _inboxesStore: InboxesStore, private _websocket: Websocket) {
    makeAutoObservable(this)
    this._disposeSubscribe = reaction(() => this._activeInboxIds.join(','), this.subscribeChannels)
  }

  private get _activeInboxIds() {
    return this._inboxesStore.personalInboxes
      .filter((inbox) => !inbox.isGroupInbox)
      .map((inbox) => inbox.id)
      .sort((a, b) => a - b)
  }

  dispose = () => {
    this._disposeSubscribe?.()
    this._disposeSubscribe = null

    this._channelsMap.forEach((_, id) => this.unsubscribeChannel(id))
  }

  subscribeChannels = () => {
    if (!this._inboxesStore.hasCurrentUser) return

    const activeIds = new Set(this._activeInboxIds)

    activeIds.forEach((id) => this.subscribeChannel(id))

    this._channelsMap.forEach((_, id) => {
      if (!activeIds.has(id)) this.unsubscribeChannel(id)
    })
  }

  subscribeChannel = (id: number) => {
    if (this._channelsMap.has(id)) return

    this._channelsMap.set(id, {
      inbox: new ChannelInbox({ id }, this._websocket),
      conversation: new ChannelInboxConversation({ id }, this._websocket),
    })
  }

  unsubscribeChannel = (id: number) => {
    const channels = this._channelsMap.get(id)
    if (!channels) return

    channels.inbox.unsubscribe()
    channels.conversation.unsubscribe()
    this._channelsMap.delete(id)
  }
}
